import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import {
  EmailAnalysisBranch,
  EmailAnalysisDraft,
  EmailAnalysisEmail,
  EmailAnalysisParticipant,
  EmailAnalysisParticipantSide,
} from "@types";
import { getClassName, splitArrayIntoChunks } from "@utils";
import { InfoIcon } from "@taskpane/icons";
import {
  ChevronLeftIcon,
  DraftIcon,
  EnvelopeIcon,
  GridZoneIcon,
  NextMoveIcon,
  TargetIcon,
  WizardDraftIcon,
} from "./icons";
import { ErrorOverlay } from "../ErrorOverlay";
import { Skeleton, SkeletonSize } from "../Skeleton";
import { Tooltip, TooltipPosition, TooltipSize } from "../Tooltip";
import { Avatar, AvatarSize, AvatarVariant } from "../Avatar";
import css from "./AuthorityZone.module.scss";

export interface AuthorityZoneProps {
  currentScore?: number;
  isDraft?: boolean;
  isLoading?: boolean;
  branches?: EmailAnalysisBranch[];
  nextMove?: string;
  selectedBranch?: EmailAnalysisBranch | null;
  selectedEmail?: EmailAnalysisEmail | EmailAnalysisDraft | null;
  isSimplified?: boolean;
  onSelectBranch?: (branchId: string) => void;
  onSelectEmail?: (emailId: string) => void;
  onSelectNextMove?: (nextMove: string) => void;
}

const AuthorityZone: React.FC<AuthorityZoneProps> = ({
  currentScore,
  isDraft = false,
  isLoading = false,
  // branches = [],
  nextMove = "",
  selectedBranch = null,
  selectedEmail = null,
  isSimplified = false,
  // onSelectBranch,
  onSelectEmail,
  onSelectNextMove,
}) => {
  const [selectedEmailsGroupIndex, setSelectedEmailGroupIndex] = useState<number>(-1);
  const [gridItemsCount, setGridItemsCount] = useState<{ vertical: number; horizontal: number }>({
    vertical: 0,
    horizontal: 0,
  });

  const emailsGroups = useMemo<(EmailAnalysisEmail | EmailAnalysisDraft | string)[][]>(() => {
    if (!selectedBranch) {
      return [];
    }

    const emails = selectedBranch.emails || [];
    const draft = selectedBranch.draft || null;

    if (isDraft && draft) {
      return splitArrayIntoChunks(isSimplified ? [draft] : [...emails, draft], 5);
    }

    if (nextMove) {
      return splitArrayIntoChunks([...emails, nextMove], 5);
    }

    return splitArrayIntoChunks(selectedBranch.emails, 5);
  }, [selectedBranch]);

  const gridWrapperRef = useRef<HTMLDivElement>(null);

  const selectedEmailsGroup = emailsGroups[selectedEmailsGroupIndex];
  const hasPrevEmailGroup = selectedEmailsGroupIndex > 0;
  const hasNextEmailGroup = selectedEmailsGroupIndex < emailsGroups.length - 1;

  const renderTooltipContent = () => {
    const attrs = {
      tooltipContent: {
        className: css.tooltipContent,
      },
      tooltipDescriptionWrapper: {
        className: css.tooltipDescriptionWrapper,
      },
      tooltipDescription: {
        className: css.tooltipDescription,
      },
      tooltipText: {
        className: css.tooltipText,
      },
      tooltipTextBold: {
        className: getClassName(css.tooltipText, css.tooltipTextBold),
      },
      tooltipTextSeparator: {
        className: css.tooltipTextSeparator,
      },
      tooltipIcon: {
        className: css.tooltipIcon,
      },
      tooltipFooter: {
        className: css.tooltipFooter,
      },
      readMoreLink: {
        className: css.readMoreLink,
        target: "_blank",
        rel: "noopener noreferrer",
        href: "https://help.substrata.me/en/articles/6519583-deal-timeline-authority-zone-101",
      },
    };

    return (
      <div {...attrs.tooltipContent}>
        <span>
          The green area in the chart reflects your perceived* level of authority, per each
          touchpoint.
        </span>
        <div {...attrs.tooltipDescriptionWrapper}>
          <div {...attrs.tooltipDescription}>
            <span {...attrs.tooltipText}>
              Being positioned <span {...attrs.tooltipTextBold}>above the green area</span> may
              indicate that you are perceived as too strong or too authoritative.
            </span>
            <div {...attrs.tooltipTextSeparator} />
            <span {...attrs.tooltipText}>
              Being positioned <span {...attrs.tooltipTextBold}>inside the green area</span> may
              indicate that you convey the right level of authority to push this engagement forward.
            </span>
            <div {...attrs.tooltipTextSeparator} />
            <span {...attrs.tooltipText}>
              Being positioned <span {...attrs.tooltipTextBold}>below the green area</span> may
              indicate that your communication lacks some authority. You might be perceived as too
              needy or agreeable.
            </span>
          </div>
          <GridZoneIcon {...attrs.tooltipIcon} />
        </div>
        <div {...attrs.tooltipFooter}>
          <span>*Estimated, as perceived by the recipient</span>
          <a {...attrs.readMoreLink}>Read more</a>
        </div>
      </div>
    );
  };

  const renderVerticalGridLines = () =>
    Array.from({ length: gridItemsCount.vertical }, (_, index) => {
      const attrs = {
        gridItem: {
          className: getClassName(css.gridLine, css.gridLineVertical),
          style: {
            left: `${(index + 1) * 16}px`,
          },
        },
      };

      return <div {...attrs.gridItem} key={index} />;
    });

  const renderHorizontalGridLines = () =>
    Array.from({ length: gridItemsCount.horizontal }, (_, index) => {
      const attrs = {
        gridItem: {
          className: getClassName(css.gridLine, css.gridLineHorizontal),
          style: {
            top: `${(index + 1) * 16}px`,
          },
        },
      };

      return <div {...attrs.gridItem} key={index} />;
    });

  const getEmailItemTooltipDisclaimerText = (
    email: EmailAnalysisEmail | EmailAnalysisDraft,
    senderParticipant: EmailAnalysisParticipant | null
  ) => {
    const { scoreInBranch, index } = email;

    if (index >= selectedBranch?.theFirstReplyIndex) {
      return "actualSzScore" in email
        ? `${senderParticipant?.isUser ? "You" : "Current message"} will be ${scoreInBranch > 10 ? "above" : scoreInBranch < -10 ? "below" : "in"} the Authority Zone`
        : `${senderParticipant?.isUser ? "You are" : "Current message is"} ${scoreInBranch > 10 ? "above" : scoreInBranch < -10 ? "below" : "in"} the Authority Zone`;
    }

    if (selectedBranch?.theFirstReplyIndex >= 10000) {
      return senderParticipant?.isUser
        ? "Your vertical positioning in the Authority Zone will be determined once a reply comes in"
        : "Your vertical positioning in the Authority Zone will be determined once a reply is sent";
    }

    return "Your vertical positioning in the Authority Zone is available in the reply message and onward";
  };

  const renderEmailItemTooltipContent = (
    item: string | EmailAnalysisEmail | EmailAnalysisDraft
  ) => {
    if (typeof item === "string") {
      const attrs = {
        itemTooltipContent: {
          className: getClassName(css.itemTooltipContent, css.itemTooltipContentText),
        },
      };

      return <div {...attrs.itemTooltipContent}>Next Move</div>;
    }

    const senderParticipant =
      item.sender === EmailAnalysisParticipantSide.Left
        ? item.participants?.leftParticipant
        : item.participants?.rightParticipant;

    const toParticipant =
      item.sender === EmailAnalysisParticipantSide.Left
        ? item.participants?.rightParticipant
        : item.participants?.leftParticipant;

    const [senderParticipantFirstName, senderParticipantLastName] =
      senderParticipant?.metadata?.name?.split(" ") || [];

    const [toParticipantFirstName, toParticipantLastName] =
      toParticipant?.metadata?.name?.split(" ") || [];

    const senderParticipantName = senderParticipantLastName
      ? `${senderParticipantFirstName} ${senderParticipantLastName[0]}.`
      : senderParticipant?.email;
    const toParticipantName = toParticipantLastName
      ? `${toParticipantFirstName} ${toParticipantLastName[0]}.`
      : toParticipant?.email;

    const disclaimerText = getEmailItemTooltipDisclaimerText(item, senderParticipant);

    const attrs = {
      itemTooltipContent: {
        className: css.itemTooltipContent,
      },
      itemTooltipParticipantsWrapper: {
        className: css.itemTooltipParticipantsWrapper,
      },
      itemTooltipParticipant: {
        className: css.itemTooltipParticipant,
      },
      itemTooltipSenderParticipantAvatar: {
        size: AvatarSize.XS,
        variant: senderParticipant?.isUser ? AvatarVariant.User : AvatarVariant.Prospect,
        src: senderParticipant?.metadata?.avatarSrc,
        name: senderParticipant?.metadata?.name || senderParticipant?.email || "",
      },
      itemTooltipToParticipantAvatar: {
        size: AvatarSize.XS,
        variant: toParticipant?.isUser ? AvatarVariant.User : AvatarVariant.Prospect,
        src: toParticipant?.metadata?.avatarSrc,
        name: toParticipant?.metadata?.name || toParticipant?.email || "",
      },
      itemTooltipDisclaimer: {
        className: css.itemTooltipDisclaimer,
      },
    };

    return (
      <div {...attrs.itemTooltipContent}>
        <div {...attrs.itemTooltipParticipantsWrapper}>
          <div {...attrs.itemTooltipParticipant}>
            <span>From</span>
            <Avatar {...attrs.itemTooltipSenderParticipantAvatar} />
            <span>{senderParticipantName}</span>
          </div>
          <div {...attrs.itemTooltipParticipant}>
            <span>To</span>
            <Avatar {...attrs.itemTooltipToParticipantAvatar} />
            <span>{toParticipantName}</span>
          </div>
        </div>
        <div {...attrs.itemTooltipDisclaimer}>
          <InfoIcon />
          <span>{disclaimerText}</span>
        </div>
      </div>
    );
  };

  const renderEmailItems = () => {
    if (isLoading) {
      return null;
    }

    return (selectedEmailsGroup || []).map((item) => {
      const isNextMove = typeof item === "string";
      const isDraftItem = !isNextMove && "actualSzScore" in item;
      const key = isNextMove ? item : item.messageId;
      const ItemIconComponent = isDraftItem ? DraftIcon : EnvelopeIcon;
      const topPosition = isNextMove ? 50 : 100 - (50 + (item.scoreInBranch / 60) * 100);
      const newTopPosition =
        typeof currentScore === "number" ? 100 - (50 + (currentScore / 60) * 100) : null;

      const isAboveNewPosition = topPosition < newTopPosition;

      const attrs = {
        column: {
          className: getClassName(
            css.column,
            !isNextMove && item.messageId === selectedEmail?.messageId && css.columnActive,
            !isNextMove &&
              item.sender === EmailAnalysisParticipantSide.Left &&
              item.participants?.leftParticipant?.isUser &&
              css.columnUser
          ),
        },
        itemMarker: {
          className: css.itemMarker,
          style: {
            top: `calc(${topPosition}% + 26px)`,
          },
        },
        draftItemMarker: {
          className: getClassName(css.itemMarker, css.itemMarkerDraft),
          style: isAboveNewPosition
            ? {
                top: `calc(${topPosition}% + 28px)`,
                height: `calc(${newTopPosition - topPosition}% - 32px)`,
              }
            : {
                top: `calc(${newTopPosition}% + 28px)`,
                height: `calc(${topPosition - newTopPosition}% - 32px)`,
              },
        },
        itemWrapper: {
          className: css.itemWrapper,
          style: {
            top: `${topPosition}%`,
          },
        },
        newItemWrapper: {
          className: css.itemWrapper,
          style: {
            top: `${newTopPosition}%`,
          },
        },
        itemTooltip: {
          size: TooltipSize.XS,
          content: renderEmailItemTooltipContent(item),
          arrow: false,
          autoPosition: !isNextMove,
          positionOffset: 4,
          position: isNextMove ? TooltipPosition.Top : TooltipPosition.Bottom,
        },
        nextMoveItem: {
          className: getClassName(
            css.item,
            css.itemNextMove,
            newTopPosition !== null && css.itemNoHover
          ),
        },
        targetIcon: {
          className: css.targetIcon,
        },
        item: {
          className: getClassName(
            css.item,
            isNextMove && css.itemNextMove,
            !isNextMove && item.messageId === selectedEmail?.messageId && css.itemActive,
            !isNextMove &&
              item.sender === EmailAnalysisParticipantSide.Left &&
              item.participants?.leftParticipant?.isUser &&
              css.itemUser,
            newTopPosition !== null && css.itemNoHover
          ),
          onClick: () => {
            if (isNextMove && onSelectNextMove) {
              onSelectNextMove(item);
              return;
            }

            if (!isNextMove && onSelectEmail) {
              onSelectEmail(item.messageId);
            }
          },
        },
      };

      return (
        <div {...attrs.column} key={key}>
          {!isDraftItem ? <div {...attrs.itemMarker} /> : null}
          {isDraftItem && newTopPosition !== null ? <div {...attrs.draftItemMarker} /> : null}
          <div {...attrs.itemWrapper}>
            {isSimplified ? (
              <div {...attrs.item}>{isNextMove ? <NextMoveIcon /> : <ItemIconComponent />}</div>
            ) : (
              <Tooltip {...attrs.itemTooltip}>
                <div {...attrs.item}>{isNextMove ? <NextMoveIcon /> : <ItemIconComponent />}</div>
              </Tooltip>
            )}
          </div>
          {isDraftItem && newTopPosition !== null ? (
            <div {...attrs.newItemWrapper}>
              <div {...attrs.nextMoveItem}>
                <TargetIcon {...attrs.targetIcon} />
                <WizardDraftIcon />
              </div>
            </div>
          ) : null}
        </div>
      );
    });
  };

  const renderDateItems = () => {
    if (isLoading || !selectedEmailsGroup) {
      return Array.from({ length: 5 }, (_, index) => {
        const attrs = {
          dateItem: {
            className: css.dateItem,
          },
          skeleton: {
            size: SkeletonSize.S,
            width: 35,
          },
        };

        return (
          <div {...attrs.dateItem} key={index}>
            <Skeleton {...attrs.skeleton} />
            <Skeleton {...attrs.skeleton} />
          </div>
        );
      });
    }

    return (selectedEmailsGroup || []).map((item) => {
      const isNextMove = typeof item === "string";
      const isDraft = !isNextMove && !!item.platformDraftId;
      const key = isNextMove ? item : item.messageId;
      const dateString =
        isNextMove || isDraft ? "" : DateTime.fromISO(item.sentAt).toFormat("LLL d");
      const timeString =
        isNextMove || isDraft ? "" : DateTime.fromISO(item.sentAt).toFormat("h:mm a").toLowerCase();

      const attrs = {
        dateItem: {
          className: getClassName(
            css.dateItem,
            !isNextMove && item.messageId === selectedEmail?.messageId && css.dateItemActive
          ),
        },
      };

      return (
        <div {...attrs.dateItem} key={key}>
          {isDraft ? <span>Draft</span> : null}
          {isNextMove ? (
            <>
              <span>Next</span>
              <span>move</span>
            </>
          ) : null}
          {dateString ? <span>{dateString}</span> : null}
          {timeString ? <span>{timeString}</span> : null}
        </div>
      );
    });
  };

  const attrs = {
    container: {
      className: getClassName(
        css.container,
        isLoading && css.containerLoading,
        isSimplified && css.containerSimplified
      ),
    },
    header: {
      className: css.header,
    },
    title: {
      className: css.title,
    },
    titleTooltip: {
      contentClassName: css.tooltip,
      disabled: isLoading,
      size: TooltipSize.XS,
      arrow: false,
      autoPosition: true,
      position: TooltipPosition.Bottom,
      content: renderTooltipContent(),
      positionOffset: 4,
      positionOverride: {
        left: 16,
      },
    },
    chartWrapper: {
      className: css.chartWrapper,
    },
    chart: {
      className: css.chart,
    },
    zonesWrapper: {
      className: css.zonesWrapper,
    },
    zoneGreenAbove: {
      className: getClassName(css.zone, css.zoneGreenAbove),
    },
    zoneGreen: {
      className: getClassName(css.zone, css.zoneGreen),
    },
    zoneGreenBelow: {
      className: getClassName(css.zone, css.zoneGreenBelow),
    },
    zoneRedAbove: {
      className: getClassName(css.zone, css.zoneRedAbove),
    },
    zoneRed: {
      className: getClassName(css.zone, css.zoneRed),
    },
    gridWrapper: {
      ref: gridWrapperRef,
      className: css.gridWrapper,
    },
    topGreenZoneBorder: {
      className: getClassName(css.gridLine, css.gridLineHorizontal, css.gridLineTopGreenZoneBorder),
    },
    bottomGreenZoneBorder: {
      className: getClassName(
        css.gridLine,
        css.gridLineHorizontal,
        css.gridLineBottomGreenZoneBorder
      ),
    },
    columnsWrapper: {
      className: css.columnsWrapper,
    },
    datesWrapper: {
      className: css.datesWrapper,
    },
    prevButton: {
      className: getClassName(css.button, css.buttonPrev),
      onClick: () => {
        if (hasPrevEmailGroup) {
          setSelectedEmailGroupIndex(selectedEmailsGroupIndex - 1);
        }
      },
    },
    nextButton: {
      className: getClassName(css.button, css.buttonNext),
      onClick: () => {
        if (hasNextEmailGroup) {
          setSelectedEmailGroupIndex(selectedEmailsGroupIndex + 1);
        }
      },
    },
    errorOverlay: {
      isOpened: !isLoading && !emailsGroups.length,
      className: css.errorOverlay,
      render: () => {
        const attrs = {
          emptyContent: {
            className: css.emptyContent,
          },
        };

        return (
          <div {...attrs.emptyContent}>
            <InfoIcon />
            <span>No data to display yet</span>
          </div>
        );
      },
    },
  };

  useLayoutEffect(() => {
    if (emailsGroups.length) {
      setSelectedEmailGroupIndex(emailsGroups.length - 1);
    }
  }, [emailsGroups]);

  useLayoutEffect(() => {
    const gridWrapper = gridWrapperRef.current;
    const handleResize = () => {
      if (gridWrapper) {
        const { width, height } = gridWrapper.getBoundingClientRect();

        setGridItemsCount(() => ({
          vertical: Math.ceil(width / 16),
          horizontal: Math.ceil(height / 16),
        }));
      }
    };

    const observer =
      typeof window.ResizeObserver !== "undefined" ? new ResizeObserver(handleResize) : null;

    if (gridWrapper) {
      handleResize();
      observer?.observe(gridWrapper);
    }

    return () => {
      if (gridWrapper) {
        observer?.unobserve(gridWrapper);
      }

      observer?.disconnect();
    };
  }, []);

  return (
    <div {...attrs.container}>
      {!isSimplified ? (
        <div {...attrs.header}>
          <div {...attrs.title}>
            <span>Authority Zone</span>
            <Tooltip {...attrs.titleTooltip} />
          </div>
        </div>
      ) : null}
      <div {...attrs.chartWrapper}>
        {hasPrevEmailGroup && !isSimplified ? (
          <button {...attrs.prevButton}>
            <ChevronLeftIcon />
          </button>
        ) : null}
        <div {...attrs.chart}>
          <div {...attrs.zonesWrapper}>
            <div {...attrs.zoneGreenAbove} />
            <div {...attrs.zoneGreen} />
            <div {...attrs.zoneGreenBelow} />
            <div {...attrs.zoneRedAbove} />
            <div {...attrs.zoneRed} />
          </div>
          <div {...attrs.gridWrapper}>
            {renderVerticalGridLines()}
            {renderHorizontalGridLines()}
            <div {...attrs.topGreenZoneBorder} />
            <div {...attrs.bottomGreenZoneBorder} />
          </div>
          <div {...attrs.columnsWrapper}>{renderEmailItems()}</div>
        </div>
        {hasNextEmailGroup && !isSimplified ? (
          <button {...attrs.nextButton}>
            <ChevronLeftIcon />
          </button>
        ) : null}
        {!isSimplified ? <ErrorOverlay {...attrs.errorOverlay} /> : null}
      </div>
      {!isSimplified ? <div {...attrs.datesWrapper}>{renderDateItems()}</div> : null}
    </div>
  );
};

export default AuthorityZone;
